import React, { useEffect, useMemo, useState } from 'react';
import { Card, Typography, Spin, Alert } from 'antd';
import { useSelector } from 'react-redux';

import type { RootState } from '@/store';
import {
  getDashboardMetric17,
  type DashboardMetric17WorkWeeksRemaining,
} from '@/services/dashboardService';

const { Text } = Typography;

export type EX1WorkWeeksRemainingWidgetProps = {
  title?: string;
};

const formatEndDate = (iso: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;

  return d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

export const EX1WorkWeeksRemainingWidget: React.FC<
  EX1WorkWeeksRemainingWidgetProps
> = ({ title = 'Work weeks remaining in audit period' }) => {
  // Get annualReportId from Redux
  const { selectedReport } = useSelector(
    (state: RootState) => state.annualReport,
  );
  const annualReportId = selectedReport?.annualReportId;

  const [data, setData] = useState<DashboardMetric17WorkWeeksRemaining | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      if (!annualReportId) {
        setData(null);
        setLoading(false);
        setErrorMessage(null);
        return;
      }

      setLoading(true);
      setErrorMessage(null);

      try {
        const res = await getDashboardMetric17(annualReportId);
        if (!isMounted) return;
        setData(res);
      } catch (err: any) {
        if (!isMounted) return;
        setData(null);
        setErrorMessage(
          err?.message ||
            err?.Message ||
            'Failed to load Metric 17 (Work weeks remaining).',
        );
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [annualReportId]);

  const remainingWeeks = useMemo(() => {
    const w = data?.remainingWeeks ?? 0;
    // keep it sane: no negative weeks
    return Math.max(0, Math.round(w));
  }, [data]);

  const endDateText = useMemo(() => {
    const formatted = formatEndDate(data?.fiscalYearEndDate ?? '');
    return formatted ? `Until ${formatted}` : '';
  }, [data]);

  return (
    <Card title={title}>
      <div style={{ minHeight: 140, display: 'flex', alignItems: 'center' }}>
        {loading ? (
          <div style={{ width: '100%', textAlign: 'center' }}>
            <Spin />
          </div>
        ) : errorMessage ? (
          <Alert
            type='error'
            message='Dashboard Metric 17 Error'
            description={errorMessage}
            showIcon
          />
        ) : !data ? (
          <Alert
            type='info'
            message='No data'
            description='Fiscal year end date was not returned.'
            showIcon
          />
        ) : (
          <div style={{ width: '100%', textAlign: 'center' }}>
            <div
              style={{
                fontSize: 64,
                fontWeight: 800,
                lineHeight: 1,
                color: '#2563EB',
              }}
            >
              {remainingWeeks}
            </div>

            <div style={{ marginTop: 6, fontSize: 16, fontWeight: 600 }}>
              Work Weeks Remaining
            </div>

            <div style={{ marginTop: 10 }}>
              <Text type='secondary'>{endDateText}</Text>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default EX1WorkWeeksRemainingWidget;
